
import { useCallback, useMemo, useState } from 'react';
import { addDays, addWeeks, startOfDay, startOfWeek } from '@client/shared/calendar/week-grid.ts';

export type CalendarRange = 'week' | 'day';

export interface UseCalendarViewArgs {
  readonly initialAnchor?: Date;
  readonly zone: string;
  readonly range?: CalendarRange;
}

export interface CalendarView {
  readonly anchor: Date;
  readonly range: CalendarRange;
  step: (delta: number) => void;
  goToday: () => void;
  setRange: (range: CalendarRange) => void;
}

function stepAnchor(
  anchor: Date,
  zone: string,
  range: CalendarRange,
  delta: number,
): Date {
  return range === 'day'
    ? addDays(startOfDay(anchor, zone), zone, delta)
    : addWeeks(startOfWeek(anchor, zone), zone, delta);
}

export function useCalendarView({
  initialAnchor,
  zone,
  range: initialRange = 'week',
}: UseCalendarViewArgs): CalendarView {
  const [range, setRangeState] = useState<CalendarRange>(initialRange);
  const [anchor, setAnchorState] = useState<Date>(() => initialAnchor ?? new Date());

  const step = useCallback(
    (delta: number) => {
      setAnchorState((current) => stepAnchor(current, zone, range, delta));
    },
    [range, zone],
  );

  const goToday = useCallback(() => setAnchorState(new Date()), []);

  return useMemo(
    () => ({ anchor, range, step, goToday, setRange: setRangeState }),
    [anchor, range, step, goToday],
  );
}
